import { DEFAULT_GC_TIME } from '@/config';
import {
    CompetitorMediaEffectQueryArgs,
    GetCompetitorsMediaInvestmentResponse,
} from '@/dashboards/CompetitorsMediaInvestment/types';
import { competitorMediaInvestmentQueries } from '@/duckdb/competitor-media-investment';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import moment from 'moment/moment';

export type CompetitorsMediaEffectDataKeys = {
    category: string;
    variable: string;
    investment: string;
    date: string;
};

const getCompetitorsMediaInvestmentDataKeys = (
    results: GetCompetitorsMediaInvestmentResponse,
) => {
    const keyMap: CompetitorsMediaEffectDataKeys = {
        category: '',
        variable: '',
        investment: '',
        date: '',
    };

    Object.entries(results.fields).forEach(([key, value]) => {
        if (value.name === 'period_name_day') {
            keyMap.date = key;
        } else {
            keyMap[value.name] = key;
        }
    });

    return keyMap;
};

const modifyCompetitorsMediaInvestmentResponse = (
    data: GetCompetitorsMediaInvestmentResponse,
) => {
    const keyMap = getCompetitorsMediaInvestmentDataKeys(data);

    return {
        fieldKeys: keyMap,
    };
};

const fetchMediaEffectData = async ({
    startDate: startDateArg,
    endDate: endDateArg,
    ...args
}: CompetitorMediaEffectQueryArgs) => {
    const startDate = startDateArg
        ? moment(startDateArg, 'DD-MM-YYYY').format('YYYY-MM-DD')
        : '';
    const endDate = endDateArg
        ? moment(endDateArg, 'DD-MM-YYYY').format('YYYY-MM-DD')
        : '';

    const rows =
        await competitorMediaInvestmentQueries.getCompetitorMediaEffectRows({
            ...args,
            startDate,
            endDate,
        });

    const data = {
        availablePeriods: ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'],
        period: null,
        fields: {
            category: {
                label: 'Category',
                name: 'category',
                type: 'string',
            },
            variable: {
                label: 'Variable',
                name: 'variable',
                type: 'string',
            },
            investment: {
                label: 'Investment',
                name: 'investment',
                type: 'number',
            },
            period: {
                label: 'Period',
                name: 'period',
                type: 'date',
            },
            period_name_day: {
                label: 'Period Name',
                name: 'period_name_day',
                type: 'string',
            },
        },
        rows,
    };

    return {
        ...data,
        ...modifyCompetitorsMediaInvestmentResponse(
            data as GetCompetitorsMediaInvestmentResponse,
        ),
    };
};

export const competitorMediaEffectQueryOptions = (
    args: CompetitorMediaEffectQueryArgs,
) => {
    return queryOptions({
        queryKey: [
            'competitor-media-effect',
            args.period,
            args.startDate,
            args.endDate,
            args.competitorImpactVariables,
        ],
        queryFn: () =>
            args.competitorImpactVariables.length
                ? fetchMediaEffectData(args)
                : Promise.resolve({
                      availablePeriods: [
                          'daily',
                          'weekly',
                          'monthly',
                          'quarterly',
                          'yearly',
                      ],
                      period: null,
                      fields: {
                          category: {
                              label: 'Category',
                              name: 'category',
                              type: 'string',
                          },
                          variable: {
                              label: 'Variable',
                              name: 'variable',
                              type: 'string',
                          },
                          investment: {
                              label: 'Investment',
                              name: 'investment',
                              type: 'number',
                          },
                          period: {
                              label: 'Period',
                              name: 'period',
                              type: 'date',
                          },
                          period_name_day: {
                              label: 'Period Name',
                              name: 'period_name_day',
                              type: 'string',
                          },
                      },
                      rows: [],
                      fieldKeys: {
                          category: 'category',
                          variable: 'variable',
                          investment: 'investment',
                          date: 'period_name_day',
                      },
                  }),
        placeholderData: keepPreviousData,
        staleTime: Infinity,
        gcTime: DEFAULT_GC_TIME,
    });
};
